import React, { useEffect, useRef, useState } from 'react';
import { isPlainLeftClick } from '../routing.js';

/* Inline Lucide-style icons (24x24, stroke 2). Self-contained — no icon lib. */
const I = {
  actuals: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18" />
      <path d="M9 21V9" />
    </svg>
  ),
  budgets: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 4 4 5-5" />
    </svg>
  ),
  forecasts: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M14 7h7v7" />
    </svg>
  ),
  supportStack: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
      <path d="M8 4v16" />
      <path d="M16 4v16" />
    </svg>
  ),
  cashMonitor: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4" />
      <path d="M8 3v4" />
      <path d="M3 10h18" />
      <path d="M7 15h4" />
      <path d="M14 16l2 2 3-4" />
    </svg>
  ),
  synthesis: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="5" />
      <circle cx="16" cy="16" r="5" />
      <path d="M12 12l0 0" />
    </svg>
  ),
  drivers: (
    /* Sliders icon — three horizontal dials with knobs. Reads as
       "the levers you tune to drive your data" (recurring schedules,
       model drivers, piecewise overrides). */
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="9" cy="6" r="2" fill="currentColor" />
      <circle cx="15" cy="12" r="2" fill="currentColor" />
      <circle cx="7" cy="18" r="2" fill="currentColor" />
    </svg>
  ),
  fcc: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20h20" />
      <path d="M4 20V10l8-5 8 5v10" />
      <path d="M9 20v-6h6v6" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 0 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 0 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  ),
  chev: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  /* Sidebar collapse icon — panel with slight fold graphic, reads as
     "the thing on the left pane" so clicking is intuitively the toggle. */
  collapse: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
      <path d="M14 10l-2 2 2 2" />
    </svg>
  ),
  /* Search (magnifying glass) — for the inline filter input. Same visual
     language as Ledger's lucide search. */
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  ),
  /* X (close) — for the filter clear button. */
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  ),
};

/* v8 Path E — nested navigation. Children render as a collapsible sub-list
   under each module. Section IDs correspond either to an existing subTab
   key (Actuals has native subTab state) or to a SectionCard `sectionId`
   anchor within the tab page (others scroll-to). */
export const SIDEBAR_ITEMS = [
  {
    id: 'actuals', label: 'Actuals', sub: 'Books · close · history',
    hint: 'Balance Sheet · Income Statement', icon: 'actuals', shortcut: 'g c',
    children: [
      { id: 'assets',      label: 'Assets',               hint: 'Current and non-current assets with tier rollup' },
      { id: 'liabilities', label: 'Liabilities',          hint: 'Current liabilities, long-term debt, payables, and obligations' },
      { id: 'equity',      label: 'Equity',               hint: 'Owner equity + retained earnings + suspense reconciliation' },
      { id: 'income',      label: 'Income Statement',     hint: 'Selected Ledger revenue, expenses, and net income' },
      { id: 'primary-income', label: 'Revenue Detail',    hint: 'Ledger revenue rows for the selected Book or Shelf scope' },
      { id: 'formal',      label: 'Formal statements',    hint: 'IS / BS / CF over Period × Density × Direction window' },
      { id: 'comparative', label: 'Comparative Tables',   hint: 'Every Actuals KPI as a horizon table — sticky headers, dense rows' },
    ],
  },
  {
    id: 'budgets', label: 'Budgets', sub: 'Plan · recurring · ops',
    hint: 'Recurring · AR/AP · DSO/DPO', icon: 'budgets', shortcut: 'g m',
    children: [
      { id: 'ratios',          label: 'Financial Ratios',    hint: '15 ratios × 5 timepoints — liquidity, solvency, profitability, efficiency' },
      { id: 'recurring',       label: 'Recurring subs',      hint: 'Monthly + yearly + quarterly subscriptions with category rollup' },
      { id: 'payables',        label: 'AP cadence',          hint: 'AP outflows by counterparty — labels configurable in Settings → Piecewise' },
      { id: 'ar-streams',      label: 'AR streams',          hint: 'AR inflows by counterparty + contribution collection rates' },
      { id: 'working-capital', label: 'DSO · DPO · CCC',     hint: 'Days Sales Outstanding · Days Payables Outstanding · Cash Conversion Cycle' },
      { id: 'formal',          label: 'Formal statements',   hint: 'IS / BS / CF — managerial view with source-state labeled history' },
      { id: 'comparative',     label: 'Comparative Tables',  hint: 'Every Budgets KPI as a horizon table' },
    ],
  },
  {
    id: 'forecasts', label: 'Forecasts', sub: 'Runway · projection · strategy',
    hint: '12mo forecast · 3-statement · DCF', icon: 'forecasts', shortcut: 'g f',
    children: [
      { id: 'drivers',       label: 'Model drivers',                          hint: 'Revenue, cost, expense, finance, operations, and macro assumptions' },
      { id: 'overlays',      label: 'Macro overlays',                         hint: 'Inflation, fuel, seasonality, and other external pressure factors' },
      { id: 'comparison',    label: 'Base · Upside · Downside',               hint: 'Side-by-side model-state comparison — MoM / QoQ / YoY / Hybrid' },
      { id: 'model-assumptions', label: 'Forecast controls',                  hint: 'Forecast-only controls for drivers, macro overlays, seasonality, and operations' },
      { id: 'formal',        label: 'Formal statements',                      hint: 'IS / BS / CF projected across the forecast horizon' },
      { id: 'comparative',   label: 'Comparative Tables',                     hint: 'Every Forecasts KPI as a horizon table' },
    ],
  },
  {
    id: 'support-stack', label: 'Support Stack', sub: 'Sub-ledgers · schedules · trackers · intents',
    hint: 'Read-only support evidence map; posted Ledger JEs remain actual truth',
    icon: 'supportStack', shortcut: 'g u',
    children: [
      { id: 'sub-ledgers', label: 'Sub-ledgers', hint: 'Support ledgers behind GL truth for loans, assets, intangibles, and carrying balances' },
      { id: 'schedules',   label: 'Schedules',   hint: 'Recognition, repayment, allocation, and valuation timing plans' },
      { id: 'trackers',    label: 'Trackers',    hint: 'Operational status, variance, exception, and run-history monitors' },
      { id: 'write-intents', label: 'Write intents', hint: 'Impact-ranked proposals that hand off to Ledger audit and amendment routes' },
    ],
  },
  {
    id: 'cash-monitor', label: 'Cash Monitor', sub: 'Daily runway · gaps',
    hint: 'Actual cash · expected receipts · committed obligations',
    icon: 'cashMonitor', shortcut: 'g h',
    children: [
      { id: 'daily-plan',  label: 'Daily plan',  hint: 'Day-by-day opening cash, receipts, obligations, optional payments, and warnings' },
      { id: 'receipts',    label: 'Receipts',    hint: 'Expected receipts labeled as assumptions until Ledger emits open AR behavior' },
      { id: 'obligations', label: 'Obligations', hint: 'Committed obligations from current Lens schedules and source-local demo posture' },
      { id: 'plan-variants', label: 'Plan variants', hint: 'Pay-on-due-date, agreed schedule, and tight-budget comparison' },
      { id: 'warnings',    label: 'Warnings',    hint: 'Cash gaps, cash floor breaks, and receipt timing risk' },
    ],
  },
  {
    id: 'synthesis', label: 'Synthesis', sub: 'Compose any view',
    hint: 'Cross-module composition · variance · sandbox', icon: 'synthesis', shortcut: 'g s',
    children: [
      { id: 'trajectory',    label: 'Trajectory chart',     hint: 'Liquid position, debt, dedicated streams, and recurring revenue across the horizon' },
      { id: 'variance',      label: 'Variance waterfall',   hint: 'Bridge from Actuals start to Forecasts end via Budgets steps' },
      { id: 'tornado',       label: 'Sensitivity tornado',  hint: 'Which drivers move the bottom line most — ranked impact bars' },
      { id: 'contribution',  label: 'Contribution margin',  hint: 'Per-revenue-stream gross, direct cost, and margin' },
      { id: 'ledger-projection', label: 'Ledger projection', hint: 'Frozen local response panel with proof matrix, guardrails, and lab state' },
      { id: 'formal',        label: 'Formal statements',    hint: 'Composed IS / BS / CF — Actuals + Budgets + Forecasts blend' },
      { id: 'comparative',   label: 'Comparative Tables',   hint: 'Every Synthesis KPI as a horizon table' },
    ],
  },
  {
    id: 'fcc', label: 'Command', sub: 'Private model sandbox',
    hint: 'Liquid · NC-Assets · NC-Liabilities · journal entries · simulations', icon: 'fcc', shortcut: 'g b',
    children: [
      { id: 'liquid',           label: 'Liquid position',          hint: 'Bank, cash, and short-term financing detail' },
      { id: 'nc-assets-liabilities', label: 'NC-Assets · NC-Liabilities', hint: 'Long-lived assets and long-term obligations. Buy / sell / payoff actions live here.' },
      { id: 'journal-entries',  label: 'Journal entries',          hint: 'Post / draft balanced adjusting JEs — flows into every module automatically' },
      { id: 'transactions',     label: 'Simulated transactions',   hint: 'Central audit ledger — every override across Actuals / Budgets / Forecasts / FCC / Settings' },
      { id: 'three-stmt',       label: '3-Statement Model',        hint: 'Projected IS / BS / CF over the command horizon — same engine as Forecasts' },
      { id: 'comparative',      label: 'Comparative Tables',       hint: 'Every command KPI as a horizon table' },
    ],
  },
  {
    id: 'drivers', label: 'Model Drivers', sub: 'Enterprise model stack',
    hint: 'Finance · revenue · cost · expense · operations · macro levers',
    icon: 'drivers', shortcut: 'g d',
    children: [
      { id: 'recurring',    label: 'Recurring schedules',              hint: 'Month-range editor for recurring revenue, costs, expenses, obligations, and operations' },
      { id: 'model-drivers', label: 'Universal model drivers',          hint: 'Top-down, bottom-up, triangulated, macro, seasonal, and operating assumptions' },
      { id: 'piecewise',    label: 'Model schedules & pins',            hint: 'Configurable schedules and pins for the full enterprise model stack' },
    ],
  },
  {
    id: 'settings', label: 'Settings', sub: 'App behavior',
    hint: 'Shared assumptions · profiles · visibility · guides · presentation', icon: 'settings', shortcut: 'g t',
    children: [
      { id: 'model-assumptions', label: 'Shared assumptions', hint: 'Canonical settings surface for reusable presets, financing, receivables, recurring revenue, and operations' },
      { id: 'profiles',     label: 'Model profiles',  hint: 'Private browser JSON: save, load, duplicate, export, or import local model states' },
      { id: 'visibility',   label: 'Account visibility', hint: 'Per-account show / hide — primary / secondary / dust tier filter + per-tab overrides' },
      { id: 'guides',       label: 'Guide controls',     hint: 'Tooltips, popovers, drill-downs, modified badges — every guidance layer toggle' },
      { id: 'presentation', label: 'Presentation',       hint: 'Density · period count · table orientation · statement style · period comparison' },
      { id: 'snapshot',     label: 'Snapshot reference', hint: 'Read-only — what data snapshot is currently loaded (version + as-of date)' },
    ],
  },
];

/* Parse a route key `'module'` or `'module:section'` into its parts. */
export function parseRoute(route) {
  const [moduleId, sectionId = null] = String(route || '').split(':');
  return { moduleId, sectionId };
}

/* Build a route key from module + optional section. */
export function buildRoute(moduleId, sectionId) {
  return sectionId ? `${moduleId}:${sectionId}` : moduleId;
}

export default function Sidebar({ activeTab, onSelect, collapsed, releaseInfo }) {
  const { moduleId: activeModule, sectionId: activeSection } = parseRoute(activeTab);
  const [expanded, setExpanded] = useState(() => ({ [activeModule]: true }));
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  /* UX rule v3 (2026-04-25): focus and expand are separate actions.
     Programmatic navigation (keyboard chord, Edit-in-X deep link, breadcrumb
     back) NEVER auto-expands a module — the user's explicit expand state
     wins. The constructor seeds `expanded = { [activeModule]: true }` for
     the initial mount so the landing module IS expanded; after that, expand
     changes are explicit (chevron, or second-click on focused chip). */

  /* Sidebar inline filter (2026-04-30 evening) — parity with the Ledger
     sidebar filter (Shell.tsx). Type to narrow visible modules + children;
     ArrowUp/Down navigates the highlight across module + section boundaries;
     Enter opens the highlighted row; Esc clears (or blurs if empty); × button
     clears via mouse. Substring of the query is wrapped in <mark>.

     Design notes:
     - When the filter is active, modules with matching children are *visually*
       expanded WITHOUT mutating `expanded` state, so clearing the filter
       restores the user's prior expansion exactly.
     - A module-level match (label / sub / hint) shows the entire children list.
     - A child-level match shows only the matching children.
     - The filter is hidden when the sidebar is `collapsed` (no labels visible
       to filter against anyway).
     - There's no global ⌘K palette in Lens, so no kbd hint here — the input
       is the only search affordance. */
  const [filter, setFilter] = useState('');
  const [highlightedIdx, setHighlightedIdx] = useState(0);
  const filterInputRef = useRef(null);
  const filterQ = filter.trim().toLowerCase();
  const isFiltering = filterQ.length > 0;

  const matchesQ = (s) => !!(s && String(s).toLowerCase().includes(filterQ));

  /* Per-module filter calc — moduleHit means parent label/sub/hint matched.
     visibleChildren is the children list as it should render under filter. */
  const filteredItems = isFiltering
    ? SIDEBAR_ITEMS
        .map((it) => {
          const moduleHit = matchesQ(it.label) || matchesQ(it.sub) || matchesQ(it.hint);
          const allChildren = Array.isArray(it.children) ? it.children : [];
          const visibleChildren = moduleHit
            ? allChildren
            : allChildren.filter((ch) => matchesQ(ch.label) || matchesQ(ch.hint));
          const hasMatch = moduleHit || visibleChildren.length > 0;
          return { ...it, _moduleHit: moduleHit, _visibleChildren: visibleChildren, _hasMatch: hasMatch };
        })
        .filter((it) => it._hasMatch)
    : SIDEBAR_ITEMS.map((it) => ({
        ...it,
        _moduleHit: false,
        _visibleChildren: Array.isArray(it.children) ? it.children : [],
        _hasMatch: true,
      }));

  /* Flat list of every navigable row in render order — drives ArrowUp/Down
     across module + child boundaries. Includes parent rows (their click
     destination is the module overview) and visible child rows. */
  const flatRows = [];
  filteredItems.forEach((it) => {
    flatRows.push({ route: it.id, moduleId: it.id, sectionId: null });
    const showChildren = isFiltering
      ? it._visibleChildren.length > 0
      : !collapsed && !!expanded[it.id];
    if (showChildren) {
      it._visibleChildren.forEach((ch) => {
        flatRows.push({ route: `${it.id}:${ch.id}`, moduleId: it.id, sectionId: ch.id });
      });
    }
  });
  const safeHighlightedIdx = flatRows.length === 0
    ? -1
    : Math.min(highlightedIdx, flatRows.length - 1);
  const highlightedRoute = flatRows[safeHighlightedIdx]?.route;

  useEffect(() => {
    setHighlightedIdx(0);
  }, [filterQ]);

  function clearFilter() {
    setFilter('');
    setHighlightedIdx(0);
  }

  function handleFilterKeyDown(e) {
    if (e.key === 'Escape') {
      if (filter) {
        e.preventDefault();
        clearFilter();
      } else {
        filterInputRef.current?.blur();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      if (flatRows.length === 0) return;
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, flatRows.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      if (flatRows.length === 0) return;
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter' && highlightedRoute) {
      e.preventDefault();
      onSelect(highlightedRoute);
      clearFilter();
    }
  }

  /* Substring-highlight a label against the active filter query. Returns
     the original string when filter is empty or substring isn't found. */
  function renderLabel(label) {
    if (!isFiltering) return label;
    const i = String(label).toLowerCase().indexOf(filterQ);
    if (i < 0) return label;
    return (
      <>
        {label.slice(0, i)}
        <mark className="rounded px-0.5 bg-accent-gold/25 text-text font-semibold">
          {label.slice(i, i + filterQ.length)}
        </mark>
        {label.slice(i + filterQ.length)}
      </>
    );
  }

  const brandClass = collapsed
    ? 'flex items-center gap-[0.6rem] justify-center pb-[0.85rem] border-b border-border'
    : 'flex items-center gap-[0.6rem] px-4 pb-[0.85rem] border-b border-border';

  return (
    <aside className="h-full bg-surface border-r border-border flex flex-col py-[0.85rem] overflow-hidden z-30">
      <div className={brandClass}>
        <span className="text-[1.5rem] text-accent-gold" aria-hidden>🜂</span>
        {!collapsed && (
          <div className="flex flex-col leading-tight">
            <strong className="text-[0.95rem] text-text">Cleic Lens</strong>
            <em className="text-[0.7rem] text-text-muted not-italic">Private source view</em>
          </div>
        )}
      </div>

      {/* Inline filter — only when sidebar is expanded. */}
      {!collapsed && (
        <div className="px-[0.45rem] pt-[0.4rem] pb-[0.2rem]">
          <label className="flex w-full items-center gap-2 rounded border border-border bg-surface-el px-[0.6rem] py-[0.4rem] text-[0.78rem] text-text-muted focus-within:border-accent-gold focus-within:bg-surface-hover transition-colors cursor-text">
            <span className="shrink-0 inline-flex items-center justify-center w-[12px] h-[12px] [&_svg]:w-[12px] [&_svg]:h-[12px]" aria-hidden>
              {I.search}
            </span>
            <input
              ref={filterInputRef}
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={handleFilterKeyDown}
              placeholder="Filter modules…"
              aria-label="Filter sidebar modules"
              aria-autocomplete="list"
              aria-controls="lens-sidebar-nav-filtered"
              aria-activedescendant={
                highlightedRoute ? `lens-nav-${highlightedRoute.replace(':', '--')}` : undefined
              }
              className="min-w-0 flex-1 bg-transparent text-[0.78rem] text-text placeholder-text-dim outline-none"
            />
            {filter && (
              <button
                type="button"
                onClick={clearFilter}
                aria-label="Clear filter"
                title="Clear filter (Esc)"
                className="shrink-0 rounded p-0.5 text-text-dim hover:bg-surface-hover hover:text-text transition-colors inline-flex items-center justify-center w-[14px] h-[14px] [&_svg]:w-[12px] [&_svg]:h-[12px]"
              >
                {I.close}
              </button>
            )}
          </label>
        </div>
      )}

      <nav
        id="lens-sidebar-nav-filtered"
        role={isFiltering ? 'listbox' : undefined}
        className="flex flex-col gap-[0.15rem] px-[0.45rem] py-[0.6rem] flex-1 overflow-y-auto"
        aria-label="Primary"
      >
        {!collapsed && isFiltering && filteredItems.length === 0 && (
          <p className="px-[0.6rem] py-[1.5rem] text-center text-[0.72rem] text-text-dim">
            No modules match &ldquo;{filter}&rdquo;
          </p>
        )}
        {filteredItems.map((it) => {
          const isActiveModule = activeModule === it.id;
          const hasChildren = Array.isArray(it.children) && it.children.length > 0;
          /* v8 Path G fix — expansion is driven ONLY by explicit state.
             The previous `|| isActiveModule` fallback meant clicking the
             active module's chip couldn't collapse it — the active flag
             would re-force it open on every render.

             FILTER OVERRIDE (2026-04-30): when the inline filter is active
             AND this module has visible children, force-expand the module
             so the matches are visible. This does NOT mutate `expanded`
             state — it's a render-time override, so clearing the filter
             restores the user's prior expansion exactly. */
          const isExpanded = !collapsed && hasChildren && (
            isFiltering
              ? it._visibleChildren.length > 0
              : !!expanded[it.id]
          );
          const isModuleHighlighted = isFiltering && highlightedRoute === it.id;

          /* Proposal 7 final — chip is a real <a href> so middle-click /
             ctrl-click / cmd-click opens the module overview in a new tab
             via native browser behavior. The chevron expand-toggle is now
             a sibling <button> to the right of the anchor (previously
             nested inside a <div role="button">, which prevented chip
             being a real link). UX simplification: clicking the chip
             ALWAYS navigates to the module's overview; the chevron is the
             only way to toggle expand/collapse. The old hover-revealed
             "overview" mini-button is removed as redundant with the
             anchor click. */
          const chipHref = '/' + it.id;
          const chipClasses = collapsed
            ? 'flex-1 flex items-center gap-[0.7rem] justify-center py-[0.6rem] px-0 text-[0.88rem] text-left font-[inherit] no-underline cursor-pointer transition-colors hover:bg-surface-hover hover:text-text'
            : 'flex-1 flex items-center gap-[0.7rem] px-[0.7rem] py-[0.55rem] text-[0.88rem] text-left font-[inherit] no-underline cursor-pointer transition-colors hover:bg-surface-hover hover:text-text';
          /* When the inline filter is active and this module is the
             arrow-highlighted row, paint the gold ring (parity with Ledger).
             The filter-highlight wins over the activeModule chrome since
             the user is in "search mode" and that focus should dominate. */
          const rowState = isModuleHighlighted
            ? 'bg-[rgba(240,180,41,0.10)] border-[rgba(240,180,41,0.55)] text-text ring-1 ring-[rgba(240,180,41,0.45)]'
            : isActiveModule && !activeSection
              ? 'bg-surface-el border-[rgba(240,180,41,0.40)] text-text shadow-[inset_3px_0_0_#f0b429]'
              : isActiveModule
                ? 'text-text'
                : 'text-text-muted';
          return (
            <div key={it.id} className="flex flex-col">
              <div className={`flex items-stretch border border-transparent rounded-sm ${rowState}`}>
                <a
                  href={chipHref}
                  id={`lens-nav-${it.id}`}
                  role={isFiltering ? 'option' : undefined}
                  aria-selected={isFiltering ? isModuleHighlighted : undefined}
                  className={chipClasses}
                  onClick={(e) => {
                    if (!isPlainLeftClick(e)) return; // middle / ctrl / cmd / shift — browser handles
                    e.preventDefault();
                    /* UX rule v3 (2026-04-25): focus ≠ expand. First click
                       on a non-focused panel changes focus ONLY — expand state
                       stays exactly as it was. Second click (now focused) toggles
                       expand. Chevron button (sibling) always toggles expand
                       regardless of focus.

                       Rationale: predictability. Clicking a panel never has a
                       hidden side-effect on expansion. Two distinct clicks,
                       two distinct actions.

                       FILTER OVERRIDE (2026-04-30): when the filter is active,
                       always navigate (don't toggle expand) and clear the
                       filter — keeps the post-click state clean. */
                    if (isFiltering) {
                      onSelect(it.id);
                      clearFilter();
                      return;
                    }
                    const wasActive = isActiveModule && !activeSection;
                    if (hasChildren && !collapsed && wasActive) {
                      // Focused → toggle expand
                      toggleExpand(it.id);
                    } else {
                      // Not focused (or no children, or sidebar collapsed) → navigate only
                      onSelect(it.id);
                    }
                  }}
                  onMouseEnter={() => {
                    if (!isFiltering) return;
                    const idx = flatRows.findIndex((r) => r.route === it.id);
                    if (idx >= 0) setHighlightedIdx(idx);
                  }}
                  title={collapsed ? `${it.label} — ${it.hint} (${it.shortcut})` : it.hint}
                  aria-current={isActiveModule && !activeSection ? 'page' : undefined}
                >
                  <span className="w-[18px] h-[18px] shrink-0 flex items-center justify-center [&_svg]:w-[18px] [&_svg]:h-[18px]" aria-hidden>{I[it.icon]}</span>
                  {!collapsed && (
                    <span className="flex flex-col flex-1 min-w-0">
                      <strong className="text-[0.85rem] font-semibold truncate">{renderLabel(it.label)}</strong>
                      <em className="text-[0.7rem] text-text-dim not-italic truncate">{renderLabel(it.sub)}</em>
                    </span>
                  )}
                  {!collapsed && !hasChildren && (
                    <span className="font-mono text-[0.7rem] text-text-dim bg-white/[0.04] px-[5px] py-px rounded-[3px]">{it.shortcut}</span>
                  )}
                </a>
                {!collapsed && hasChildren && (
                  <button
                    type="button"
                    className={`px-[0.55rem] flex items-center justify-center shrink-0 text-text-dim hover:text-accent-gold hover:bg-[rgba(240,180,41,0.08)] focus-visible:text-accent-gold transition-colors cursor-pointer border-l border-border/30 [&_svg]:w-[14px] [&_svg]:h-[14px] [&_svg]:transition-transform [&_svg]:duration-150 ${isExpanded ? '[&_svg]:rotate-90' : ''}`}
                    onClick={() => toggleExpand(it.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpand(it.id);
                      }
                    }}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${it.label} sections`}
                    aria-expanded={isExpanded}
                    title={`${isExpanded ? 'Collapse' : 'Expand'} ${it.label}`}
                  >
                    {I.chev}
                  </button>
                )}
              </div>

              {hasChildren && !collapsed && (
                /* v8 Path H — animated grid-rows collapse (0fr → 1fr). The
                   outer div transitions on grid-template-rows; the inner
                   ul is measured by the browser and auto-fits. Rendered
                   unconditionally so enter + exit animate symmetrically. */
                <div
                  className="sidebar-children-wrap"
                  data-open={isExpanded ? 'true' : 'false'}
                  aria-hidden={!isExpanded}
                >
                  <div className="sidebar-children-inner">
                    <ul className="flex flex-col gap-px pl-[2.15rem] pr-[0.3rem] pt-[2px] pb-[4px] border-l border-border/50 ml-[1.1rem] list-none">
                      {(isFiltering ? it._visibleChildren : it.children).map((ch) => {
                        /* Treat "Overview" as the module root too, so a
                           breadcrumb "back" that lands on `budgets` (no
                           section) still highlights the Overview child. */
                        const active = isActiveModule && (
                          activeSection === ch.id ||
                          (ch.id === 'overview' && !activeSection)
                        );
                        const childRoute = `${it.id}:${ch.id}`;
                        const isChildHighlighted = isFiltering && highlightedRoute === childRoute;
                        /* Proposal 7 — real <a href> so middle-click / ctrl-click opens
                           in a new tab via native browser behavior. isPlainLeftClick
                           intercepts ONLY regular left-click (no modifiers) for
                           client-side navigation. */
                        const childHref = `/${it.id}/${ch.id}`;
                        return (
                          <li key={ch.id}>
                            <a
                              href={childHref}
                              id={`lens-nav-${it.id}--${ch.id}`}
                              role={isFiltering ? 'option' : undefined}
                              aria-selected={isFiltering ? isChildHighlighted : undefined}
                              className={`block w-full text-left px-[0.55rem] py-[0.32rem] text-[0.76rem] rounded-sm cursor-pointer font-[inherit] transition-colors no-underline ${
                                isChildHighlighted
                                  ? 'bg-[rgba(240,180,41,0.18)] text-accent-gold font-semibold ring-1 ring-[rgba(240,180,41,0.45)]'
                                  : active
                                    ? 'bg-[rgba(240,180,41,0.12)] text-accent-gold font-semibold'
                                    : 'text-text-muted hover:bg-surface-hover hover:text-text'
                              }`}
                              onClick={(e) => {
                                if (!isPlainLeftClick(e)) return; // middle / ctrl / cmd / shift — let browser handle
                                e.preventDefault();
                                onSelect(childRoute);
                                if (isFiltering) clearFilter();
                              }}
                              onMouseEnter={() => {
                                if (!isFiltering) return;
                                const idx = flatRows.findIndex((r) => r.route === childRoute);
                                if (idx >= 0) setHighlightedIdx(idx);
                              }}
                              aria-current={active ? 'page' : undefined}
                              tabIndex={isExpanded ? 0 : -1}
                              title={ch.hint || ch.label}
                              aria-label={ch.hint ? `${ch.label} — ${ch.hint}` : ch.label}
                            >
                              <span className="inline-flex items-center gap-[6px]">
                                <span>{renderLabel(ch.label)}</span>
                                {ch.badge && (
                                  <span
                                    className="text-[0.58rem] font-bold px-[5px] py-[1px] rounded-[3px] uppercase tracking-[0.06em] bg-[rgba(240,180,41,0.14)] text-accent-gold border border-accent-gold/50"
                                    title="Non-agnostic / source-specific view"
                                  >
                                    {ch.badge}
                                  </span>
                                )}
                              </span>
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className={collapsed ? 'border-t border-border px-2 py-[0.55rem]' : 'border-t border-border px-4 py-[0.6rem]'}>
        {releaseInfo && (
          <div
            className={`cleic-human-currentness-stamp overflow-hidden text-ellipsis whitespace-nowrap font-mono font-medium leading-none tracking-normal text-text-dim ${
              collapsed
                ? 'w-full text-center text-[9px]'
                : 'mb-[0.45rem] text-[0.62rem]'
            }`}
            data-currentness-stamp={releaseInfo.currentnessStamp}
            data-human-currentness-stamp={releaseInfo.humanCurrentnessStamp}
            data-cleic-human-currentness-stamp="true"
            data-app-channel={releaseInfo.channel}
            data-release-marker={releaseInfo.releaseMarker}
            data-source-marker="cleic-human-currentness-stamp-20260518a"
            title={releaseInfo.detail}
            aria-label={releaseInfo.humanCurrentnessStamp}
          >
            {collapsed ? releaseInfo.channelLabel : releaseInfo.humanCurrentnessStamp}
          </div>
        )}
        {!collapsed && (
          <p className="text-[0.7rem] text-text-dim m-0 italic">
            Every output is a prayer. Every file handled with Love and Respect.
          </p>
        )}
      </div>
    </aside>
  );
}

export const SidebarIcons = I;
